/**
 * Downloading the main pages' code ahead of a click.
 *
 * A page's code is otherwise only fetched on its first visit, and the switch
 * waits for it with the current page still showing, so a first click seemed
 * to do nothing for a second or more. Once fetched, import() resolves from
 * webpack's cache and the page opens at once.
 *
 * - prefetchView(file): fetch one page now. The top bar calls it when the
 *   pointer reaches a link, which is usually well before the click lands.
 * - preloadViews(files): fetch the rest in the background, one at a time,
 *   while the browser is idle, once per app load.
 *
 * Kept apart from Pages/index so the top bar can use it without importing
 * the page loader (which imports the layout the top bar is part of).
 */
const fetched = new Set();

export const prefetchView = file => {
  if (!file || fetched.has(file)) return;
  fetched.add(file);
  import(`./${file}`).catch(() => fetched.delete(file));
};

const whenIdle = callback =>
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(callback, { timeout: 2000 })
    : setTimeout(callback, 300);

let preloadStarted = false;

export const preloadViews = files => {
  if (preloadStarted) return;
  preloadStarted = true;
  const queue = [...files];
  const next = () => {
    const file = queue.shift();
    if (!file) return;
    if (fetched.has(file)) {
      next();
      return;
    }
    fetched.add(file);
    import(`./${file}`).catch(() => fetched.delete(file)).finally(() => whenIdle(next));
  };
  whenIdle(next);
};
